import { DateTime } from 'luxon';

import { ILibraryCuisineType } from '../../queries/fragments/libraryCuisineType';
import { IMeetingPlacePageEditorial } from '../../queries/fragments/meetingPlacePageEditorial';
import { IMeetingPlacePageEventItem } from '../../queries/fragments/meetingPlacePageEventItem';
import { IMeetingPlacePageLeisurePackageOfferItem } from '../../queries/fragments/meetingPlacePageLeisurePackageOfferItem';
import { IMeetingPlacePageNewsItem } from '../../queries/fragments/meetingPlacePageNewsItem';
import { TOccationTypes } from '../../queries/fragments/meetingPlacePageOccations';
import { IMeetingPlacePageOfferItem } from '../../queries/fragments/meetingPlacePageOfferItem';
import { IMeetingPlacePageServiceItem } from '../../queries/fragments/meetingPlacePageServiceItem';
import { TTenantTypes } from '../../queries/fragments/meetingPlacePageTenantList';
import { generateCorrectSlug } from '../generate-correct-slug';
import {
  EditorialCard,
  LeisurePackageOfferCard,
  ListCardCategories,
  ListCardType,
  OccationEventCard,
  OccationNewsCard,
  OccationOfferCard,
  ServiceCard,
  SubTenantCard,
  TenantCard,
} from './interfaces/list-card.interface';
import { _numTag } from './list-page-constants';
import {
  pageIsEditorial,
  pageIsEvent,
  pageIsLeisurePackageOffer,
  pageIsOffer,
  pageIsService,
  pageIsTenantEntertainment,
  pageIsTenantFoodBeverage,
  pageIsTenantShop,
} from './type-guards';

export interface SelectInputNameProps {
  news?: string;
  events?: string;
}

export const getEventCategories = (
  eventItem: IMeetingPlacePageEventItem,
): ListCardCategories[] => {
  return eventItem.eventTypes?.map((et) => {
    return { id: et.id, name: et.name };
  });
};

export const getOfferCategories = (
  offerItem: IMeetingPlacePageOfferItem,
): ListCardCategories[] => {
  if (offerItem?.typeOfOffer?.internalName !== 'regularOffer') {
    return [{ id: offerItem.id, name: offerItem?.typeOfOffer?.name }];
  } else {
    return [];
  }
};

export const getTenantCardCategories = (
  tenantPage: TTenantTypes,
): ListCardCategories[] => {
  const tenantCategories: ListCardCategories[] = [];

  const getSecondaryCategories = (
    secondaryCategories: ListCardCategories[],
  ): ListCardCategories[] => {
    return secondaryCategories?.map((category) => {
      return {
        id: category.id,
        name: category.name,
      };
    });
  };

  if (tenantPage && tenantPage.primaryCategory) {
    tenantCategories.push({
      id: tenantPage.primaryCategory.id,
      name: tenantPage.primaryCategory.name,
    });
    if (tenantPage && tenantPage.secondaryCategories) {
      tenantCategories.push(
        ...getSecondaryCategories(tenantPage.secondaryCategories),
      );
    }
  } else {
    //Fallback
    if (tenantPage && tenantPage.brand?.primaryCategory)
      tenantCategories.push({
        id: tenantPage.brand.primaryCategory.id,
        name: tenantPage.brand.primaryCategory.name,
      });
    if (tenantPage && tenantPage.brand?.secondaryCategories) {
      tenantCategories.push(
        ...getSecondaryCategories(tenantPage.brand.secondaryCategories),
      );
    }
  }
  return tenantCategories;
};

const getTenantCardPrimaryCategories = (
  tenantPage: TTenantTypes,
): ListCardCategories[] => {
  const tenantPrimaryCategories: ListCardCategories[] = [];

  if (tenantPage && tenantPage.primaryCategory) {
    tenantPrimaryCategories.push({
      id: tenantPage.primaryCategory.id,
      name: tenantPage.primaryCategory.name,
    });
  } else {
    //Fallback
    if (tenantPage && tenantPage.brand?.primaryCategory)
      tenantPrimaryCategories.push({
        id: tenantPage.brand.primaryCategory.id,
        name: tenantPage.brand.primaryCategory.name,
      });
  }
  return tenantPrimaryCategories;
};

const createTenantCard = (page: TTenantTypes): TenantCard => {
  const name = page.name ? page.name : page.brand?.name ? page.brand.name : '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageSlug = page.slug
    ? page.slug
    : page.brand?.slug
      ? page.brand?.slug
      : '';

  return {
    id: page.id,
    name,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage ? page.coverImage : page.brand?.coverImage,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: pageSlug,
    }),
    categories: getTenantCardCategories(page),
    logo: page?.logo ? page.logo : page.brand?.logo,
    cardType: 'tenant',
    node_locale: page?.node_locale,
    giftCard: page?.giftCard,
    loyaltyCard: page?.loyaltyCard,
    ...('cuisine' in page && {
      cuisineTypes: page.cuisine?.map((ct: ILibraryCuisineType) => ({
        id: ct.id,
        name: ct.name,
      })),
    }),
    ...('priceLevel' in page && {
      priceLevel: {
        id: page?.priceLevel?.id,
        name: page?.priceLevel?.name,
      },
    }),
    ...('hasOffer' in page && {
      offerPage: page?.hasOffer,
    }),
  };
};

export const createSubTenantCard = (page: TTenantTypes): SubTenantCard => {
  const name = page.name ? page.name : page.brand?.name ? page.brand.name : '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageSlug = page.slug
    ? page.slug
    : page.brand?.slug
      ? page.brand?.slug
      : '';

  return {
    id: page.id,
    name,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage ? page.coverImage : page.brand?.coverImage,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: pageSlug,
    }),
    categories: getTenantCardPrimaryCategories(page),
    logo: page?.logo ? page.logo : page.brand?.logo,
    cardType: 'subTenant',
    node_locale: page?.node_locale,
  };
};

const createOfferCard = (
  page: IMeetingPlacePageOfferItem,
): OccationOfferCard => {
  const name = page.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  return {
    id: page.id,
    name,
    updatedAt: page.updatedAt,
    createdAt: page.createdAt,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page.coverImage,
    date: DateTime.fromISO(page.date),
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: page.slug,
    }),
    validFrom: DateTime.fromISO(page.validFrom),
    validTo: DateTime.fromISO(page.validTo),
    categories: getOfferCategories(page),
    typeOfOffer: page?.typeOfOffer,
    tags: page.tags,
    logo: page?.tenant?.logo
      ? page.tenant.logo
      : page?.tenant?.brand?.logo
        ? page.tenant.brand.logo
        : undefined,
    cardType: 'offer',
    brand: page?.tenant?.name
      ? page.tenant.name
      : page?.tenant?.brand?.name
        ? page.tenant.brand.name
        : '',
    node_locale: page.node_locale,
  };
};

const createEventCard = (
  page: IMeetingPlacePageEventItem,
  selectInputName?: SelectInputNameProps,
): OccationEventCard => {
  const name = page.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  return {
    id: page.id,
    name,
    updatedAt: page.updatedAt,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page.coverImage,
    caption: page.caption,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: page.slug,
    }),
    startDate: DateTime.fromISO(page.startDate),
    endDate: DateTime.fromISO(page.endDate),
    hideInListFrom: DateTime.fromISO(page.hideInListFrom),
    eventTypes: page.eventTypes,
    floorPlanId: page.floorPlanId,
    categories: selectInputName?.events
      ? [
          {
            id: page.id,
            name: selectInputName?.events ? selectInputName?.events : '',
          },
        ]
      : getEventCategories(page),
    // logo: page?.tenant?.logo
    //   ? page.tenant.logo
    //   : page?.tenant?.brand?.logo
    //   ? page.tenant.brand.logo
    //   : undefined,
    cardType: 'event',
    node_locale: page.node_locale,
    priority: page?.priority,
  };
};

const createNewsCard = (
  page: IMeetingPlacePageNewsItem,
  selectInputName?: SelectInputNameProps,
): OccationNewsCard => {
  const name = page.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  return {
    id: page.id,
    name,
    isdatevisible: page.isdatevisible,
    updatedAt: page.updatedAt,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page.coverImage,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: page.slug,
    }),
    date: DateTime.fromISO(page.date),
    title: page.name,
    caption: page.caption,
    hideInListFrom: DateTime.fromISO(page.hideInListFrom),
    categories: [
      { id: page.id, name: selectInputName?.news ? selectInputName?.news : '' },
    ],
    logo: page?.tenant?.logo
      ? page.tenant.logo
      : page?.tenant?.brand?.logo
        ? page.tenant.brand.logo
        : undefined,
    cardType: 'news',
    node_locale: page.node_locale,
  };
};

const createServiceCard = (page: IMeetingPlacePageServiceItem): ServiceCard => {
  const serviceType = page?.serviceType;

  const firstCharIsNum =
    !isNaN(parseInt(serviceType?.name.slice(0, 1))) || false;
  return {
    id: page.id,
    name: page.name
      ? page.name
      : page.serviceType?.name
        ? page.serviceType.name
        : '',
    firstChar: firstCharIsNum ? _numTag : serviceType?.name.slice(0, 1),
    firstCharIsNum,
    image: serviceType?.coverImage,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name: page.name,
      node_locale: page.node_locale,
      slug: page.slug,
    }),
    logo: undefined,
    categories: serviceType?.serviceCategory
      ? [serviceType?.serviceCategory]
      : [],
    cardType: 'service',
    node_locale: page.node_locale,
  };
};

const createEditorialCard = (
  page: IMeetingPlacePageEditorial,
): EditorialCard => {
  const name = page.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  return {
    id: page.id,
    name,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page.image,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: page.slug,
    }),
    title: page.name,
    categories: [],
    cardType: 'editorial',
    node_locale: page.node_locale,
  };
};

const createLeisurePackageOfferCard = (
  page: IMeetingPlacePageLeisurePackageOfferItem,
): LeisurePackageOfferCard => {
  const name = page.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));

  return {
    id: page.id,
    name,
    titleLeisurePackageOffer: page.titleLeisurePackageOffer,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page.coverImage,
    slug: generateCorrectSlug({
      customizedSlug: page.customizedSlug,
      name,
      node_locale: page.node_locale,
      slug: page.slug,
    }),
    cardTag: page?.cardTag,
    offerValidFrom: DateTime.fromISO(page?.offerValidFrom || ''),
    offerValidTill: DateTime.fromISO(page?.offerValidTill || ''),
    categories: [],
    salesPrice: page.salesPrice,
    originalPrice: page?.originalPrice,
    currency: page?.currency,
    cardType: 'leisurePackageOffer',
    node_locale: page.node_locale,
  };
};

export const createListCardArray = (
  list:
    | TTenantTypes[]
    | TOccationTypes[]
    | IMeetingPlacePageServiceItem[]
    | IMeetingPlacePageEditorial[]
    | IMeetingPlacePageLeisurePackageOfferItem[],
  selectInputName?: SelectInputNameProps,
): ListCardType[] => {
  return list?.map((page) => getCardArray(page, selectInputName));
};

export const calculateActiveFilters = (
  searchString: string | null,
  selectedCategory?: string | null,
  selectedType?: string | null,
  selectedPriceLevel?: string | null,
  filterGiftCard?: boolean | null,
  filterLoyaltyCard?: boolean | null,
  filterOffer?: boolean | null,
) => {
  let activeFilters = 0;
  if (searchString && searchString.length > 0) {
    activeFilters++;
  }
  if (selectedCategory && selectedCategory.length > 0) {
    activeFilters++;
  }
  if (selectedType && selectedType.length > 0) {
    activeFilters++;
  }
  if (selectedPriceLevel && selectedPriceLevel.length > 0) {
    activeFilters++;
  }
  if (filterGiftCard || filterLoyaltyCard || filterOffer) {
    activeFilters++;
  }
  return activeFilters;
};

export const getCardArray = (
  page:
    | TTenantTypes
    | TOccationTypes
    | IMeetingPlacePageServiceItem
    | IMeetingPlacePageEditorial
    | IMeetingPlacePageLeisurePackageOfferItem,
  selectInputName?: SelectInputNameProps,
):
  | TenantCard
  | OccationEventCard
  | OccationNewsCard
  | OccationOfferCard
  | ServiceCard
  | EditorialCard
  | LeisurePackageOfferCard => {
  if (
    pageIsTenantShop(page) ||
    pageIsTenantEntertainment(page) ||
    pageIsTenantFoodBeverage(page)
  ) {
    return createTenantCard(page);
  }

  if (pageIsOffer(page)) {
    return createOfferCard(page);
  }

  if (pageIsEvent(page)) {
    return createEventCard(page, selectInputName);
  }
  if (pageIsService(page)) {
    return createServiceCard(page);
  }
  if (pageIsEditorial(page)) {
    return createEditorialCard(page);
  }
  if (pageIsLeisurePackageOffer(page)) {
    return createLeisurePackageOfferCard(page);
  }
  return createNewsCard(page, selectInputName);
};
